'use client'

import { useContext, useEffect, useRef, useState } from "react";
import classes from "./login_container.module.css";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useRouter, useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import { useMutation } from "@tanstack/react-query";
import { resendVerifyEmail, getMyUser, manualAuth, sendErrorLog } from "@/utils/http";
import SafeImage from "@/components/common/safe_image";
import mailbox from '@/assets/png/mailbox.png';
import waving_hand from '@/assets/png/waving-hand.png';
import CustomButton from "../buttons/button";
import SocialButtons from "./social_buttons";
import CustomSVG from "../svg";
import AlertTriangle from '@/assets/svg/icons/alert-triangle.svg';
import ArrowRight from '@/assets/svg/icons/arrow-right.svg';
import Eye from '@/assets/svg/icons/eye.svg';
import X from '@/assets/svg/icons/x.svg';
import EyeOff from '@/assets/svg/icons/eye-off.svg';
import TextInput from "../inputs/text_input";
import FormControl from "../checks_and_radios/form_control";
import Link from "next/link";
import AuthContext from "@/providers/auth-context";
import ToastContext from "@/providers/toast-context";
import LinkWithParams from "@/components/link_with_params/link_with_params";
import { useSession } from "next-auth/react";
import { getRecaptchaToken, isRecaptchaError, getRecaptchaErrorMessage } from "@/utils/recaptcha";
import RecaptchaV2Modal from "../recaptcha_v2_modal/recaptcha_v2_modal";
import axios from "axios";
import { getOrganizerPublicLandingSlug } from "@/utils/organizer-public-landing";

export default function LoginContainer({ success, setSucces, is_provider, is_modal }) {
    const searchParams = useSearchParams();
    const emailInputRef = useRef(null);
    const passwordInputRef = useRef(null);
    const visiblePasswordInputRef = useRef(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [noEmail, setNoEmail] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [user, setUser] = useState(undefined);
    const [isLoginPending, setIsLoginPending] = useState(false);
    const [isLoginSlow, setIsLoginSlow] = useState(false);
    const [showRecaptchaV2Modal, setShowRecaptchaV2Modal] = useState(false);
    const [pendingLoginData, setPendingLoginData] = useState(null);
    const loginStartedAtRef = useRef(0);
    const slowLoginTimerRef = useRef(null);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { setShowLogin, setShowLoginState, redirectURL, setRedirectURL, loginIsProvider } = useContext(AuthContext);
    const toastCtx = useContext(ToastContext);
    const { data: session, status } = useSession();

    const reportLoginIssue = (phase, error) => {
        const elapsedMs = loginStartedAtRef.current ? Date.now() - loginStartedAtRef.current : 0;
        const httpStatus = error?.response?.status || error?.status || "unknown";
        const details = `phase: ${phase} | elapsed_ms: ${elapsedMs} | http_status: ${httpStatus}`;

        console.error(`[login] ${details}`);

        if (process.env.NEXT_PUBLIC_DISABLE_ERROR_LOG === "enabled") {
            sendErrorLog({
                params: {
                    message: `Login flow issue: ${phase}`,
                    stack: details,
                    url: typeof window !== "undefined" ? window.location.pathname : "SSR",
                    useragent: typeof navigator !== "undefined" ? navigator.userAgent : "SSR"
                }
            }).catch(logError => console.error("Login issue log send failed:", logError));
        }
    };

    const startLoginProgress = () => {
        loginStartedAtRef.current = Date.now();
        setIsLoginPending(true);
        setIsLoginSlow(false);
        setNoEmail(false);
        setEmailError(false);
        setPasswordError(false);
        clearTimeout(slowLoginTimerRef.current);
        slowLoginTimerRef.current = setTimeout(() => {
            setIsLoginSlow(true);
            reportLoginIssue("slow_response");
        }, 10000);
    };

    const stopLoginProgress = () => {
        clearTimeout(slowLoginTimerRef.current);
        slowLoginTimerRef.current = null;
        setIsLoginPending(false);
        setIsLoginSlow(false);
    };

    const handleEmailChange = (val) => {
        setEmail(val);
        setEmailError(false);
        setNoEmail(false);
    }

    const syncAutofilledCredentialsFromDom = () => {
        const autofilledEmail = emailInputRef?.current?.value ?? "";
        const autofilledPassword = passwordInputRef?.current?.value ?? visiblePasswordInputRef?.current?.value ?? "";

        if (!email && autofilledEmail) {
            setEmail(autofilledEmail);
        }

        if (autofilledPassword && autofilledPassword !== password) {
            setPassword(autofilledPassword);
            setPasswordError(false);
            if (!expanded) {
                setExpanded(true);
            }
        }
    }

    const handleEmailFocus = (e) => {
        // Chrome/Safari autofill can update the DOM value without firing React onChange.
        // Sync state on focus so caret/edit behavior stays consistent on first click.
        const domValue = e?.target?.value ?? "";
        if (!email && domValue) {
            setEmail(domValue);
        }

        // Password managers often fill the password slightly after the email selection.
        setTimeout(syncAutofilledCredentialsFromDom, 0);
        setTimeout(syncAutofilledCredentialsFromDom, 150);
        setTimeout(syncAutofilledCredentialsFromDom, 300);
    }

    const handlePasswordChange = (val) => {
        setPassword(val);
        setPasswordError(false);
    }

    const handlePasswordFocus = () => {
        syncAutofilledCredentialsFromDom();
    }

    const handleHiddenPasswordChange = (e) => {
        const nextPassword = e?.target?.value ?? "";

        if (nextPassword !== password) {
            setPassword(nextPassword);
            setPasswordError(false);
        }

        if (nextPassword && !expanded) {
            setExpanded(true);
        }
    }

    const handleContinue = async (e) => {
        e.preventDefault();

        if (isLoginPending) return;

        if (!expanded) {
            setExpanded(true);
            return false;
        } else {
            const currentEmail = email || emailInputRef?.current?.value || "";
            const currentPassword = password || passwordInputRef?.current?.value || visiblePasswordInputRef?.current?.value || "";
            const trimmedEmail = currentEmail.trim();
            const trimmedPassword = currentPassword.trim();

            if (currentEmail !== email) {
                setEmail(currentEmail);
            }

            if (currentPassword !== password) {
                setPassword(currentPassword);
            }

            if (!trimmedEmail) {
                setEmailError("Kötelező mező");
                return;
            }

            if (!trimmedPassword) {
                setPasswordError("Kötelező mező");
                return;
            }

            if (!executeRecaptcha) {
                reportLoginIssue("recaptcha_unavailable");
                setNoEmail(999);
                setEmailError(true);
                return;
            }

            startLoginProgress();

            try {
                const gRecaptchaToken = await getRecaptchaToken(executeRecaptcha, "login");

                // NextAuth is the source of truth for login state (session cookie). Do not call manualAuth here.
                const result = await signIn("credentials", {
                    redirect: false,
                    captcha_response: gRecaptchaToken,
                    username: trimmedEmail,
                    password: trimmedPassword,
                    remember: rememberMe
                });

                // CredentialsProvider returns { ok, error, status, url }.
                // If backend requests a v2 challenge, authorize() throws -> result.error will be set.
                if (result?.error) {
                    stopLoginProgress();
                    // Try to parse structured error coming from authorize() (we throw new Error(message)).
                    let parsed;
                    try {
                        parsed = JSON.parse(result.error);
                    } catch (_) {
                        parsed = null;
                    }

                    // NextAuth sometimes returns a generic "CredentialsSignin" error and puts details
                    // into result.url as an encoded JSON message in the querystring.
                    if ((!parsed || !parsed.message) && typeof result?.url === 'string') {
                        try {
                            const u = new URL(result.url);
                            const raw = u.searchParams.get('error');
                            if (raw) {
                                // error param can be an encoded JSON string
                                try {
                                    parsed = JSON.parse(raw);
                                } catch (_) {
                                    // or a plain string
                                    parsed = { message: raw };
                                }
                            }
                        } catch (_) {
                            // ignore URL parse errors
                        }
                    }

                    if (parsed?.recaptcha_v2_required) {
                        setPendingLoginData({
                            username: trimmedEmail,
                            password: trimmedPassword,
                            remember: rememberMe
                        });
                        setShowRecaptchaV2Modal(true);
                        return;
                    }

                    // Map common business errors
                    const msg = parsed?.message || result.error;
                    if (msg === "Hibás e-mail") {
                        setNoEmail(1);
                        setEmailError(true);
                    } else if (msg === "Az e-mail cím aktiválás alatt van") {
                        setNoEmail(2);
                        setEmailError(true);
                    } else if (msg === "Hibás jelszó") {
                        setPasswordError("Megadott jelszó helytelen")
                    } else if (msg && msg.toLowerCase().includes('recaptcha')) {
                        // If it's a recaptcha-related failure but not the v2-required flag, show a generic error.
                        setNoEmail(999);
                        setEmailError(true);
                    } else {
                        reportLoginIssue("credentials_callback_error", { status: result.status });
                        setNoEmail(999);
                        setEmailError(true);
                    }
                } else if (!result?.ok) {
                    stopLoginProgress();
                    reportLoginIssue("credentials_callback_empty_result", { status: result?.status });
                    setNoEmail(999);
                    setEmailError(true);
                }
            } catch (error) {
                stopLoginProgress();
                reportLoginIssue(isRecaptchaError(error) ? "recaptcha_exception" : "credentials_callback_exception", error);
                
                // reCAPTCHA v2 fallback check
                if (error?.response?.data?.recaptcha_v2_required) {
                    setPendingLoginData({
                        username: trimmedEmail,
                        password: trimmedPassword,
                        remember: rememberMe
                    });
                    setShowRecaptchaV2Modal(true);
                    return;
                }
                
                // Handle other axios errors
                if (error?.response?.data?.message === "Hibás e-mail") {
                    setNoEmail(1);
                    setEmailError(true);
                } else if (error?.response?.data?.message === "Az e-mail cím aktiválás alatt van") {
                    setNoEmail(2);
                    setEmailError(true);
                } else if (error?.response?.data?.message === "Hibás jelszó") {
                    setPasswordError("Megadott jelszó helytelen")
                } else if (isRecaptchaError(error)) {
                    setNoEmail(999);
                    setEmailError(true);
                } else {
                    setNoEmail(999);
                    setEmailError(true);
                }
            }
        }
    }

    // reCAPTCHA v2 modal handlers
    const handleV2Success = async (token) => {
        if (!pendingLoginData) return;
        
        startLoginProgress();

        try {
            if (!executeRecaptcha) {
                console.log("not available to execute recaptcha")
                throw new Error('executeRecaptcha not available');
            }

            // We still send a v3 token in `captcha_response` (backend schema expects it),
            // but we must NOT send the v2 token there.
            const gRecaptchaToken = await getRecaptchaToken(executeRecaptcha, "login");

            setShowRecaptchaV2Modal(false);
            setPendingLoginData(null);

            // IMPORTANT: do NOT call manualAuth() here.
            // NextAuth needs to be the single source of truth, otherwise we'll call /login twice and
            // re-use the same v2 token, which can make the second attempt fail.
            const result = await signIn("credentials", {
                redirect: false,
                captcha_response: gRecaptchaToken,
                username: pendingLoginData.username,
                password: pendingLoginData.password,
                remember: pendingLoginData.remember,
                recaptcha_v2_response: token
            });

            if (result?.error) {
                stopLoginProgress();
                let parsed;
                try {
                    parsed = JSON.parse(result.error);
                } catch (_) {
                    parsed = null;
                }

                if ((!parsed || !parsed.message) && typeof result?.url === 'string') {
                    try {
                        const u = new URL(result.url);
                        const raw = u.searchParams.get('error');
                        if (raw) {
                            try {
                                parsed = JSON.parse(raw);
                            } catch (_) {
                                parsed = { message: raw };
                            }
                        }
                    } catch (_) {
                        // ignore
                    }
                }

                const msg = parsed?.message || result.error;
                if (msg === "Hibás e-mail") {
                    setNoEmail(1);
                    setEmailError(true);
                } else if (msg === "Az e-mail cím aktiválás alatt van") {
                    setNoEmail(2);
                    setEmailError(true);
                } else if (msg === "Hibás jelszó") {
                    setPasswordError("Megadott jelszó helytelen")
                } else {
                    reportLoginIssue("recaptcha_v2_callback_error", { status: result.status });
                    setNoEmail(999);
                    setEmailError(true);
                }
            } else if (!result?.ok) {
                stopLoginProgress();
                reportLoginIssue("recaptcha_v2_callback_empty_result", { status: result?.status });
                setNoEmail(999);
                setEmailError(true);
            }
        } catch (error) {
            stopLoginProgress();
            reportLoginIssue("recaptcha_v2_callback_exception", error);
            setShowRecaptchaV2Modal(false);
            setPendingLoginData(null);
            setNoEmail(999);
            setEmailError(true);
            
            // Show error toast for v2 validation failure
            toastCtx.addToast({
                type: "error",
                header_text: "Bejelentkezés sikertelen",
                sub_text: "Biztonsági ellenőrzés sikertelen. Kérjük, próbáld újra később!"
            });
        }
    };

    const handleV2Close = () => {
        setShowRecaptchaV2Modal(false);
        setPendingLoginData(null);
        // Form fields remain intact
    };

    const { mutate: resend } = useMutation({
        mutationFn: resendVerifyEmail,
        onSuccess: (response) => {
            if (response.success) {
                setSucces(true);
                setNoEmail(false);
            } else {
                if (response.message === "A felhasználó elérte a regisztrációs e-mail küldési limitet") {
                    setNoEmail(3);
                    setEmailError(true);
                }
            }
        },
        onError: (error) => {
            console.error("Resend verification email error:", error);
            if (isRecaptchaError(error)) {
                setNoEmail(999);
                setEmailError(true);
            }
        }
    });

    const getAndSetUser = async () => {
        try {
            const user = await getMyUser();
            setUser({ ...user?.response?.user });
        } catch (error) {
            stopLoginProgress();
            reportLoginIssue("identity_request_exception", error);
            setNoEmail(999);
            setEmailError(true);
        }
    }

    const resendEmail = async () => {
        if (!executeRecaptcha) {
            console.log("not available to execute recaptcha")
            return;
        }

        const gRecaptchaToken = await getRecaptchaToken(executeRecaptcha, "login");

        resend({
            params: {
                email: email,
                captcha_response: gRecaptchaToken,
            }
        })
    }

    useEffect(() => {
        if (searchParams.get('invitation_redirect')) setRedirectURL(`/megosztas-elfogadas/${searchParams.get('invitation_redirect')}`);
    }, [])

    useEffect(() => {
        // Keep React state aligned with browser autofill shortly after mount/open.
        let checks = 0;
        const maxChecks = 12;
        const interval = setInterval(() => {
            syncAutofilledCredentialsFromDom();
            checks += 1;
            if (checks >= maxChecks) {
                clearInterval(interval);
            }
        }, 250);

        return () => {
            clearInterval(interval);
        };
    }, [expanded]);

    useEffect(() => () => clearTimeout(slowLoginTimerRef.current), []);

    // Ha a login modal egy publikus szervezői landing oldalról nyílt, sikeres bejelentkezés után egyenesen a saját munkaterületre (ill. az adott modulra) ugorjon, ne ugyanazt a landing oldalt töltse újra
    const resolveOrganizerRedirect = async (token) => {
        const organizerSlug = getOrganizerPublicLandingSlug((redirectURL || "").split("?")[0]);

        if (organizerSlug === null) {
            window.location.href = (redirectURL ? redirectURL : "/szervezom");
            return;
        }

        try {
            const weddings = await axios.get(`${process.env.NEXT_PUBLIC_BACKEND_URL}/wedding`, {
                headers: { Authorization: `Bearer ${token}` },
            }).then((r) => r.data);
            const weddingId = weddings?.response?.weddings?.[0]?.id || 0;

            window.location.href = weddingId ? `/szervezom/${weddingId}${organizerSlug ? `/${organizerSlug}` : ""}` : "/szervezom/0";
        } catch (_error) {
            window.location.href = "/szervezom";
        }
    };

    useEffect(() => {
        if (status === "authenticated" && session.user) {
            if (user) {
                if (user?.isProvider) {
                    window.location.href = (redirectURL ? redirectURL : "/hirdetoi-fiok");
                } else {
                    resolveOrganizerRedirect(session.user.token);
                }
            } else {
               getAndSetUser();
            }
        }
    }, [session, status, user]);

    let err_msg = "";
    switch (noEmail) {
        case 1:
            err_msg = "A megadott e-mail címmel nem található fiók. Probálkozz másikkal, vagy hozz létre újat a regisztrációs gombra kattintva.";
            break;
        case 2:
            err_msg = "A megadott e-mail cím aktiválása folyamatban van, az e-mail újraküldéséhez kattints a linkre.";
            break;
        case 3:
            err_msg = "A felhasználó elérte a regisztrációs e-mail küldési limitet"
            break;
        case 999:
            err_msg = "A bejelentkezés közben váratlan hiba történt. Kérjük, próbáld újra!";
            break;
        default:
            err_msg = "";
    }

    return <div className={`${classes["login-container"]} ${is_provider ? classes["provider-container"] : ""} ${is_modal ? classes["modal-container"] : ""} position-relative`} ><div className="d-flex flex-row align-items-center w-100 overflow-auto">
        {success
            ? <div className={`${classes["verify-email-container"]} ${is_modal ? "p-0" : ""}`} style={{ margin: "auto" }} >
                <div className="d-flex flex-column flex-start gap-4 align-self-stretch">
                    <h1 className={`${classes["login-container-header-texts-inner"]} color-gray-900 display-md-bold`}>
                        Erősítsd meg az e-mail címed
                        <SafeImage src={mailbox} width={32} height={32} priority alt="integető kéz" />
                        {is_modal && <CustomSVG className={`stroke-gray-500 ${classes["login-container-x"]}`} SvgFunction={X} height={24} width={24} onClick={() => setShowLogin(false)} />}
                    </h1>
                    <h3 className="margin-0 padding-0 color-gray-700 text-lg-regular">
                        A folytatáshoz elküldtük a linket e-mailben a <span className="text-lg-semibold">{email}</span> címre.<br /><br />
                        Ellenőrizd a spam mappát is.
                    </h3>
                </div>
                <div className="d-flex flex-row justify-content-center align-items-center align-self-center align-self-stretch gap-1 color-gray-700 text-sm-regular flex-wrap">
                    <p className="margin-0 padding-0 text-center">Nem kaptál visszaigazoló e-mailt?</p>
                    <span className="text-sm-semibold pointer text-decoration-underline" onClick={resendEmail}>Újraküldöm</span>
                </div>
            </div>
            : <div className={`${classes["login-container-inner"]}`} style={{ margin: "auto" }} >
                <div className={`${classes["login-container-header-texts"]}`}>
                    <h1 className={`${classes["login-container-header-texts-inner"]} color-gray-900 display-md-bold`} >
                        {is_provider ? "Hirdetői bejelentkezés" : "Üdvözlünk újra!"}
                        <SafeImage src={waving_hand} width={32} height={32} priority alt="integető kéz" />
                        {is_modal && <CustomSVG className={`stroke-gray-500 ${classes["login-container-x"]}`} SvgFunction={X} height={24} width={24} onClick={() => setShowLogin(false)} />}
                    </h1>
                    <h3 className="padding-0 margin-0 color-gray-500 text-xl-regular">
                        Válaszd ki a bejelentkezési lehetőséget
                    </h3>
                </div>
                <form className={`${classes["form-container"]}`} onSubmit={e => handleContinue(e)} noValidate>
                    <SocialButtons redirectURL={redirectURL} />
                    <div className="d-flex flex-row align-self-stretch align-items-center gap-6">
                        <div className="divider-200" />
                        <p className="margin-0 padding-0 color-gray-700 text-sm-regular">vagy</p>
                        <div className="divider-200" />
                    </div>
                    {noEmail && <div className={`${classes["no-email"]}`}>
                        <CustomSVG SvgFunction={AlertTriangle} className={`stroke-warning-500 flex-shrink-0`} height={24} width={24} />
                        <div className="d-flex flex-column align-self-stretch">
                            <p className="margin-0 padding-0 color-gray-700 text-xs-regular">
                                Bejelentkezés sikertelen
                            </p>
                            <p className="margin-0 padding-0 color-gray-800 text-xs-medium">
                                {err_msg}
                            </p>
                            {noEmail === 2 && <div>
                                <CustomButton size="sm" hierarchy="link-gray" type="button" onClick={resendEmail} >
                                    Újra küldés
                                </CustomButton>
                            </div>}
                        </div>
                    </div>}
                    <div className="d-flex flex-column align-self-stretch gap-2">
                        <TextInput className="w-100" type="email" value={email} onFocus={handleEmailFocus} onChange={e => handleEmailChange(e.target.value)} label="E-mail" hint={emailError ? emailError : undefined} error={emailError} placeholder={"e-mail@mail.hu"} name="email" autoComplete="username" inputRef={emailInputRef} required_mark required />
                        <input
                            ref={passwordInputRef}
                            type="password"
                            name="password"
                            autoComplete="current-password"
                            tabIndex={-1}
                            aria-hidden="true"
                            onChange={handleHiddenPasswordChange}
                            style={{
                                position: "absolute",
                                opacity: 0,
                                width: 0,
                                height: 0,
                                pointerEvents: "none"
                            }}
                        />
                        <div className={`${expanded ? "" : "d-none"} d-flex flex-column align-self-stretch`}>
                            <div className="d-flex flex-row align-self-stretch align-items-center justify-content-between">
                                <p className="padding-0 margin-0 text-sm-medium color-gray-700">Jelszó</p>
                                <Link className="padding-0 margin-0 text-decoration-none color-gray-500 text-xs-regular pointer" href={"/elfelejtett-jelszo"}>Elfelejtetted a jelszavad?</Link>
                            </div>
                            <TextInput className="w-100" type={passwordVisible ? "text" : "password"} value={password} onFocus={handlePasswordFocus} onChange={e => handlePasswordChange(e.target.value)} hint={passwordError ? passwordError : undefined} error={passwordError} placeholder={"Jelszó"} name="password_visible" autoComplete="current-password" inputRef={visiblePasswordInputRef} required_mark help_icon={passwordVisible ? EyeOff : Eye} help_icon_click={() => setPasswordVisible(!passwordVisible)} />
                        </div>
                        {expanded && <FormControl id="remember-me" theme="primary" checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} type="checkbox" size={"sm"}
                            main_text={"Emlékezz rám"}
                            sub_text={"A következő 30 napig megőrizzük a bejelentkezésedet"}
                        />}
                    </div>
                    <CustomButton className="w-100" size="lg" hierarchy="primary" icon="trailing" is_svg={true} svg={ArrowRight} disabled={isLoginPending} isLoading={isLoginPending} loadingText={isLoginSlow ? "A bejelentkezés tovább tart..." : "Bejelentkezés..."} >
                        Tovább
                    </CustomButton>
                    {is_provider
                        ? <p className="margin-0 padding-0 color-gray-700 text-sm-regular d-flex align-self-stretch justify-content-center gap-1">
                            Szeretnél hirdetőként megjelenni? <Link href="/szolgaltato/regisztracio" className="margin-0 padding-0 color-gray-700 text-sm-semibold">Hirdetői regisztráció</Link>
                        </p>
                        : loginIsProvider
                            ? <></>
                            : <div className="color-gray-700 text-sm-regular d-flex align-self-stretch justify-content-center gap-1 flex-wrap">
                                <p className="margin-0 padding-0 text-center">Még nem rendelkezel fiókkal?</p>
                                {is_modal
                                    ? <span className="margin-0 padding-0 color-gray-700 text-sm-semibold pointer" onClick={() => setShowLoginState("registration")}>Regisztráció</span>
                                    : <LinkWithParams href="/regisztracio" className="margin-0 padding-0 color-gray-700 text-sm-semibold">Regisztráció</LinkWithParams>
                                }
                            </div>
                    }
                    {is_provider
                        ? <></>
                        : (!is_modal && <>
                            <div className="d-flex flex-row align-self-stretch justify-content-center">
                                <div className="divider-200" style={{ width: 128 }} />
                            </div>
                            <div className="d-flex flex-row align-self-stretch justify-content-center">
                                <CustomButton size="md" hierarchy="tertiary-gray" icon="trailing" is_svg={true} svg={ArrowRight} href={"/szolgaltato-bejelentkezes"} rememberSearchParams>
                                    Szolgáltatói belépés
                                </CustomButton>
                            </div>
                        </>)
                    }
                </form>
            </div>
        }
        
        {/* reCAPTCHA v2 Modal */}
        <RecaptchaV2Modal
            show={showRecaptchaV2Modal}
            onClose={handleV2Close}
            onSuccess={handleV2Success}
        />
    </div></div>
}